import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineCloudUpload, AiOutlineFileExcel } from 'react-icons/ai';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs.js';
import { BrighterGreen } from '../../../app/theme/tokens/colors.js';
import { SpreadsheetUploadValidationModal } from '../components/SpreadsheetUploadValidationModal';
import { Panel } from '../../../shared/ui/panel/panels.js';
import {
    getUploadHistory,
    rollbackLatestSnapshot,
} from '../../../services/configuration/uploadHistoryService.js';
import { uploadProductsSpreadsheet } from '../../../services/configuration/spreadsheetUploadService.js';
import { BackupRollbackModal } from './BackupRollbackModal.js';
import { SendSpreadsheetModal } from './SendSpreadsheetModal.js';
import {
    Actions,
    CardHeaderRow,
    CardTitle,
    DropHint,
    DropIcon,
    DropText,
    DropTitle,
    DropZone,
    DropZoneInner,
    EmptyPreview,
    FileName,
    FilePill,
    FileSize,
    HeaderRow,
    HelperText,
    HiddenInput,
    HistoryBadge,
    HistoryDate,
    HistoryFile,
    HistoryItem,
    HistoryMain,
    HistoryWrap,
    MetaRow,
    PreCheckBox,
    PreCheckError,
    PreviewMeta,
    PreviewTable,
    PreviewTableWrap,
    Section,
    Subtitle,
    Title,
    TitleBlock,
    UploadGrid,
    formatBytes,
} from './spreadsheetUploadConfig.style.js';
import {
    buildPrecheckFromArrayBuffer,
    parseProductsFromArrayBuffer,
    parseProductsFromFile,
    validateProductsForUpload,
} from './spreadsheetUploadParser.js';

export const SpreadsheetUploadConfig = () => {
    const fileInputRef = useRef(null);
    const validatedProductsRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [parseError, setParseError] = useState('');
    const [sizeNormalization, setSizeNormalization] = useState({
        commaToDot: 0,
        numericDecimal: 0,
    });
    const [precheckProgress, setPrecheckProgress] = useState({
        stage: '',
        current: 0,
        total: 0,
    });
    const [preCheck, setPreCheck] = useState({
        sheetName: '',
        headers: [],
        rows: [],
        totalRows: 0,
    });
    const [validationOpen, setValidationOpen] = useState(false);
    const [missingColumns, setMissingColumns] = useState([]);
    const [validationMeta, setValidationMeta] = useState({
        fileName: '',
        sheetName: '',
    });

    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [uploadHistory, setUploadHistory] = useState([]);
    const [rollbackLoading, setRollbackLoading] = useState(false);
    const [backupModalOpen, setBackupModalOpen] = useState(false);

    const [sendModalOpen, setSendModalOpen] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendError, setSendError] = useState('');
    const [sendProgress, setSendProgress] = useState({
        stage: '',
        current: 0,
        total: 0,
    });

    const sizeWarningMessage = useMemo(() => {
        if (missingColumns.length > 0) return '';
        if (`${parseError ?? ''}`.trim().length > 0) return '';
        const hasNormalization =
            (sizeNormalization?.commaToDot ?? 0) > 0 ||
            (sizeNormalization?.numericDecimal ?? 0) > 0;
        if (!hasNormalization) return '';

        return 'Aviso: a coluna size foi ajustada automaticamente para usar ponto (.) no decimal.';
    }, [
        missingColumns.length,
        parseError,
        sizeNormalization?.commaToDot,
        sizeNormalization?.numericDecimal,
    ]);

    const requiredColumns = useMemo(
        () => [
            'sap_code',
            'operation_group',
            'product_segment_id',
            'description',
            'inv_niv',
            'flow_o2',
            'adult_child',
            'weight',
            'eletrical_supply',
            'generic',
            'product_category_id',
            'product_subcategory_id',
            'type',
            'layout',
            'indicate_rule',
            'size',
            'explanation',
            'technical_information',
            'explanation1',
        ],
        []
    );

    const normalizeColumnName = useCallback((value) => {
        const text = `${value ?? ''}`
            .replace(/^\uFEFF/, '')
            .trim()
            .toLowerCase();

        const noAccents = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return noAccents
            .replace(/\s+/g, '_')
            .replace(/-+/g, '_')
            .replace(/[^a-z0-9_]/g, '')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '');
    }, []);

    const subtitleText =
        'Envie uma planilha Excel para atualização em lote dos produtos.';

    const accept = useMemo(
        () =>
            '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
        []
    );

    const formatDateTime = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return `${value}`;
        return d.toLocaleString('pt-BR');
    };

    const resetSelection = useCallback(() => {
        setSelectedFile(null);
        setParseError('');
        setIsParsing(false);
        setPrecheckProgress({ stage: '', current: 0, total: 0 });
        validatedProductsRef.current = null;
        setPreCheck({ sheetName: '', headers: [], rows: [], totalRows: 0 });
        setMissingColumns([]);
        setValidationOpen(false);
        setValidationMeta({ fileName: '', sheetName: '' });
        setSizeNormalization({
            commaToDot: 0,
            numericDecimal: 0,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const onSelectFile = useCallback((file) => {
        if (!file) return;
        setValidationMeta({ fileName: file?.name || '', sheetName: '' });
        setSelectedFile(file);
        setSizeNormalization({
            commaToDot: 0,
            numericDecimal: 0,
        });
    }, []);

    const canSend =
        !!selectedFile &&
        !isParsing &&
        !parseError &&
        missingColumns.length === 0 &&
        preCheck.totalRows > 0;

    const loadUploadHistory = useCallback(() => {
        let cancelled = false;
        setHistoryLoading(true);
        setHistoryError('');

        getUploadHistory(50)
            .then((data) => {
                if (cancelled) return;
                setUploadHistory(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                if (cancelled) return;
                setUploadHistory([]);
                setHistoryError(e?.message || 'Falha ao buscar histórico de uploads.');
            })
            .finally(() => {
                if (!cancelled) setHistoryLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const successHistory = useMemo(() => {
        if (!Array.isArray(uploadHistory)) return [];
        return uploadHistory.filter(
            (item) => `${item?.status ?? ''}`.toUpperCase() === 'SUCCESS'
        );
    }, [uploadHistory]);

    const rollbackTarget = useMemo(() => {
        // Mostra o upload com sucesso anterior ao "Atual" (que é o mais recente SUCCESS).
        // Se não existir, desabilita rollback (conforme regra solicitada).
        return successHistory.length >= 2 ? successHistory[1] : null;
    }, [successHistory]);

    const rollbackDisabledMessage = useMemo(() => {
        if (successHistory.length === 0)
            return 'Não existe nenhum upload com sucesso para usar como base de rollback.';
        if (successHistory.length === 1)
            return 'Não existe upload anterior com sucesso para reverter.';
        return '';
    }, [successHistory.length]);

    const canRollback = !!rollbackTarget && !rollbackDisabledMessage;

    const handleSendConfirm = useCallback(async () => {
        if (!selectedFile) return;
        try {
            setSendLoading(true);
            setSendError('');
            const validatedProducts = validatedProductsRef.current;

            // Fallback de segurança (se o usuário conseguiu abrir o modal antes do término)
            const parsed = !validatedProducts
                ? await parseProductsFromFile({
                    file: selectedFile,
                    normalizeColumnName,
                    requiredColumns,
                })
                : { products: validatedProducts };

            const normalizationFromSend = parsed?.normalizations?.size;
            if (
                (normalizationFromSend?.commaToDot ?? 0) > 0 ||
                (normalizationFromSend?.numericDecimal ?? 0) > 0
            ) {
                setSizeNormalization({
                    commaToDot: normalizationFromSend.commaToDot ?? 0,
                    numericDecimal: normalizationFromSend.numericDecimal ?? 0,
                });
            }

            if (!validatedProducts) {
                await validateProductsForUpload({
                    products: parsed.products,
                    maxErrors: 20,
                    onProgress: ({ current, total }) =>
                        setSendProgress({ stage: 'Validando…', current, total }),
                });
            }

            const productsToSend = (parsed.products || []).map((p) => {
                // remove internal helper field
                const rest = { ...(p || {}) };
                delete rest.__rowNumber;
                return rest;
            });

            if (!productsToSend.length) {
                throw new Error('Nenhuma linha de produto encontrada para enviar.');
            }

            setSendProgress({
                stage: 'Enviando…',
                current: productsToSend.length,
                total: productsToSend.length,
            });

            await uploadProductsSpreadsheet({
                fileName: selectedFile.name,
                products: productsToSend,
            });
            setSendModalOpen(false);
            resetSelection();
            await loadUploadHistory();
        } catch (e) {
            setSendError(e?.message || 'Falha ao enviar a planilha.');
        } finally {
            setSendLoading(false);
            setSendProgress({ stage: '', current: 0, total: 0 });
        }
    }, [
        loadUploadHistory,
        normalizeColumnName,
        requiredColumns,
        resetSelection,
        selectedFile,
    ]);

    const handleRollbackConfirm = useCallback(async () => {
        if (!canRollback) return;
        try {
            setRollbackLoading(true);
            await rollbackLatestSnapshot();
            await loadUploadHistory();
            setBackupModalOpen(false);
        } catch (e) {
            setHistoryError(e?.message || 'Falha ao executar rollback.');
        } finally {
            setRollbackLoading(false);
        }
    }, [canRollback, loadUploadHistory]);

    useEffect(() => loadUploadHistory(), [loadUploadHistory]);

    useEffect(() => {
        if (!selectedFile) {
            setIsParsing(false);
            setParseError('');
            setPrecheckProgress({ stage: '', current: 0, total: 0 });
            validatedProductsRef.current = null;
            setPreCheck({ sheetName: '', headers: [], rows: [], totalRows: 0 });
            setSizeNormalization({
                commaToDot: 0,
                numericDecimal: 0,
            });
            return;
        }

        let cancelled = false;
        const reader = new FileReader();

        setIsParsing(true);
        setParseError('');
        setPrecheckProgress({ stage: 'Lendo planilha…', current: 0, total: 0 });
        validatedProductsRef.current = null;
        setPreCheck({ sheetName: '', headers: [], rows: [], totalRows: 0 });
        setMissingColumns([]);
        setValidationOpen(false);
        setSizeNormalization({
            commaToDot: 0,
            numericDecimal: 0,
        });

        reader.onload = async (event) => {
            try {
                if (cancelled) return;
                const arrayBuffer = event?.target?.result;
                if (!arrayBuffer) throw new Error('Não foi possível ler o arquivo.');

                const result = buildPrecheckFromArrayBuffer({
                    arrayBuffer,
                    normalizeColumnName,
                    requiredColumns,
                    previewRowCount: 3,
                });

                setValidationMeta({
                    fileName: selectedFile?.name || '',
                    sheetName: result.sheetName,
                });

                if (result.headers.length === 0 && result.totalRows === 0) {
                    setPreCheck({
                        sheetName: result.sheetName,
                        headers: [],
                        rows: [],
                        totalRows: 0,
                    });
                    return;
                }

                setMissingColumns(result.missingColumns);
                setValidationOpen(true);

                if (result.missingColumns.length > 0) {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }

                setPreCheck({
                    sheetName: result.sheetName,
                    headers: result.headers,
                    rows: result.rows,
                    totalRows: result.totalRows,
                });

                // Validação completa já no pré-check: bloqueia o botão Enviar e mostra erros aqui
                setPrecheckProgress({
                    stage: 'Validando linhas…',
                    current: 0,
                    total: Math.max(result.totalRows, 0),
                });

                const parsedFull = parseProductsFromArrayBuffer({
                    arrayBuffer,
                    normalizeColumnName,
                    requiredColumns,
                });

                const normalization = parsedFull?.normalizations?.size;
                if (
                    (normalization?.commaToDot ?? 0) > 0 ||
                    (normalization?.numericDecimal ?? 0) > 0
                ) {
                    setSizeNormalization({
                        commaToDot: normalization.commaToDot ?? 0,
                        numericDecimal: normalization.numericDecimal ?? 0,
                    });
                }

                setPrecheckProgress({
                    stage: 'Validando linhas…',
                    current: 0,
                    total: parsedFull.products.length,
                });

                await validateProductsForUpload({
                    products: parsedFull.products,
                    maxErrors: 20,
                    onProgress: ({ current, total }) =>
                        setPrecheckProgress({ stage: 'Validando linhas…', current, total }),
                });

                validatedProductsRef.current = parsedFull.products;
                setPrecheckProgress({ stage: '', current: 0, total: 0 });
            } catch (err) {
                validatedProductsRef.current = null;
                setParseError(err?.message || 'Falha ao ler a planilha.');
                setSizeNormalization({
                    commaToDot: 0,
                    numericDecimal: 0,
                });
            } finally {
                if (!cancelled) setIsParsing(false);
            }
        };

        reader.onerror = () => {
            if (cancelled) return;
            setIsParsing(false);
            setParseError('Falha ao ler o arquivo.');
            setPrecheckProgress({ stage: '', current: 0, total: 0 });
            validatedProductsRef.current = null;
            setSizeNormalization({
                commaToDot: 0,
                numericDecimal: 0,
            });
        };

        reader.readAsArrayBuffer(selectedFile);

        return () => {
            cancelled = true;
            try {
                reader.abort();
            } catch (_) {
                // ignore
            }
        };
    }, [normalizeColumnName, requiredColumns, selectedFile]);

    return (
        <Section>
            <HeaderRow>
                <TitleBlock>
                    <Title>Upload da Planilha</Title>
                    <Subtitle>{subtitleText}</Subtitle>
                </TitleBlock>

                <Actions>
                    <SecondaryButton
                        type="button"
                        onClick={resetSelection}
                        disabled={!selectedFile}
                        title={
                            !selectedFile
                                ? 'Selecione um arquivo para habilitar'
                                : 'Limpar arquivo selecionado'
                        }
                    >
                        Limpar
                    </SecondaryButton>

                    <PrimaryButton
                        type="button"
                        disabled={!canSend || sendLoading}
                        title={
                            !selectedFile
                                ? 'Selecione um arquivo'
                                : missingColumns.length > 0
                                    ? 'Corrija as colunas faltantes'
                                    : 'Enviar planilha'
                        }
                        onClick={() => {
                            setSendError('');
                            setSendModalOpen(true);
                        }}
                    >
                        {sendLoading ? 'Enviando…' : 'Enviar'}
                    </PrimaryButton>
                </Actions>
            </HeaderRow>

            <SendSpreadsheetModal
                open={sendModalOpen}
                onClose={() => (!sendLoading ? setSendModalOpen(false) : null)}
                loading={sendLoading}
                error={sendError}
                progress={sendProgress}
                canConfirm={canSend}
                onConfirm={handleSendConfirm}
            />

            <UploadGrid>
                <Panel style={{ padding: 15 }}>
                    <SpreadsheetUploadValidationModal
                        open={validationOpen}
                        onClose={() => setValidationOpen(false)}
                        missingColumns={missingColumns}
                        fileName={validationMeta?.fileName}
                        sheetName={validationMeta?.sheetName}
                        dataValidationMessage={
                            missingColumns.length === 0 ? parseError : ''
                        }
                        warningMessage={sizeWarningMessage}
                    />

                    <DropZone
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const file = e.dataTransfer?.files?.[0];
                            onSelectFile(file);
                        }}
                    >
                        <HiddenInput
                            ref={fileInputRef}
                            type="file"
                            accept={accept}
                            onChange={(e) => {
                                const file = e.target?.files?.[0];
                                onSelectFile(file);
                            }}
                        />

                        <DropZoneInner>
                            <DropIcon>
                                <AiOutlineCloudUpload size={22} color={BrighterGreen} />
                            </DropIcon>
                            <DropText>
                                <DropTitle>Arraste e solte o Excel aqui</DropTitle>
                                <DropHint>
                                    ou clique para selecionar. Tipos aceitos: <b>.xlsx</b> e{' '}
                                    <b>.xls</b>
                                </DropHint>
                                <DropHint style={{ color: '#7a7a7a' }}>
                                    Tamanho recomendado: até 10 MB
                                </DropHint>
                            </DropText>
                        </DropZoneInner>

                        <MetaRow>
                            <FilePill
                                title={selectedFile?.name || 'Nenhum arquivo selecionado'}
                            >
                                <AiOutlineFileExcel size={18} color={BrighterGreen} />
                                <FileName>
                                    {selectedFile?.name || 'Nenhum arquivo selecionado'}
                                </FileName>
                            </FilePill>
                            <FileSize>
                                {selectedFile ? formatBytes(selectedFile.size) : ''}
                            </FileSize>
                            <div style={{ flex: 1 }} />
                            <SecondaryButton
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                            >
                                Escolher arquivo
                            </SecondaryButton>
                        </MetaRow>
                    </DropZone>

                    <div style={{ marginTop: 12 }}>
                        <CardTitle>Pré-check (visual)</CardTitle>
                        <PreCheckBox>
                            {!selectedFile ? (
                                <EmptyPreview>
                                    Selecione um arquivo para visualizar.
                                </EmptyPreview>
                            ) : null}
                            {selectedFile && isParsing ? (
                                <EmptyPreview>
                                    {precheckProgress.stage || 'Lendo planilha…'}
                                    {precheckProgress.total > 0
                                        ? ` ${precheckProgress.current}/${precheckProgress.total}`
                                        : ''}
                                </EmptyPreview>
                            ) : null}
                            {selectedFile && !isParsing && parseError ? (
                                <PreCheckError style={{ padding: 12 }}>
                                    {parseError}
                                </PreCheckError>
                            ) : null}

                            {selectedFile && !isParsing ? (
                                preCheck.headers.length > 0 && preCheck.rows.length > 0 ? (
                                    <PreviewTableWrap>
                                        <PreviewMeta>
                                            Total de linhas: <b>{preCheck.totalRows}</b>
                                        </PreviewMeta>
                                        <PreviewTable>
                                            <thead>
                                                <tr>
                                                    {preCheck.headers.map((h, idx) => (
                                                        <th key={`${idx}-${h}`}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preCheck.rows.map((row, rIdx) => (
                                                    <tr key={rIdx}>
                                                        {row.map((cell, cIdx) => (
                                                            <td key={cIdx} title={cell}>
                                                                {cell}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </PreviewTable>
                                    </PreviewTableWrap>
                                ) : !parseError ? (
                                    <EmptyPreview>
                                        Nenhuma linha para pré-visualizar (verifique se há cabeçalho
                                        e dados na primeira aba).
                                    </EmptyPreview>
                                ) : null
                            ) : null}
                        </PreCheckBox>
                    </div>
                </Panel>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                    <Panel style={{ padding: 15 }}>
                        <CardTitle>Últimos uploads com sucesso</CardTitle>

                        {historyLoading ? (
                            <EmptyPreview>Carregando histórico…</EmptyPreview>
                        ) : null}
                        {!historyLoading && historyError ? (
                            <PreCheckError>{historyError}</PreCheckError>
                        ) : null}

                        {!historyLoading && !historyError ? (
                            successHistory.length === 0 ? (
                                <EmptyPreview>
                                    Nenhum upload com sucesso encontrado.
                                </EmptyPreview>
                            ) : (
                                <HistoryWrap>
                                    {successHistory.slice(0, 5).map((item, idx) => (
                                        <HistoryItem
                                            key={item?.id ?? `${idx}-${item?.file_name ?? ''}`}
                                            $highlight={idx === 0}
                                        >
                                            <HistoryMain>
                                                <HistoryFile title={item?.file_name || ''}>
                                                    {item?.file_name || 'Arquivo'}
                                                </HistoryFile>
                                                <HistoryDate>
                                                    {formatDateTime(item?.uploaded_at)}
                                                </HistoryDate>
                                            </HistoryMain>
                                            {idx === 0 ? <HistoryBadge>Atual</HistoryBadge> : null}
                                        </HistoryItem>
                                    ))}
                                </HistoryWrap>
                            )
                        ) : null}
                    </Panel>

                    <Panel style={{ padding: 15 }}>
                        <CardHeaderRow>
                            <CardTitle style={{ marginBottom: 0 }}>Backup</CardTitle>
                            <SecondaryButton
                                type="button"
                                disabled={rollbackLoading || !canRollback}
                                onClick={() => (canRollback ? setBackupModalOpen(true) : null)}
                                title={
                                    canRollback
                                        ? 'Abrir confirmação de rollback'
                                        : rollbackDisabledMessage || 'Rollback indisponível'
                                }
                            >
                                Reverter
                            </SecondaryButton>
                        </CardHeaderRow>

                        <HelperText>
                            {canRollback ? (
                                <>
                                    Restaura os produtos para o estado do upload com sucesso
                                    anterior ao atual: <b>{rollbackTarget?.file_name}</b>.
                                </>
                            ) : (
                                rollbackDisabledMessage
                            )}
                        </HelperText>

                        <BackupRollbackModal
                            open={backupModalOpen}
                            onClose={() =>
                                !rollbackLoading ? setBackupModalOpen(false) : null
                            }
                            loading={rollbackLoading}
                            canConfirm={canRollback}
                            targetFileName={rollbackTarget?.file_name || ''}
                            disabledMessage={rollbackDisabledMessage}
                            onConfirm={handleRollbackConfirm}
                        />
                    </Panel>
                </div>
            </UploadGrid>
        </Section>
    );
};
